import { useMemo, useState, useCallback } from 'react';
import { SelectBox } from 'devextreme-react/select-box';
import { ClientContactsEditor } from './client-contacts-editor';
import { ProjectEditorApiService } from '~/services';

const fieldLabelStyle = { display: 'block', fontSize: '13px', fontWeight: 600, margin: '4px 0' };

function formatBillingAddress(address) {
  if (!address) return '';
  const parts = [address.description, address.street, address.city].filter(
    (p) => p && String(p).trim() !== ''
  );
  return parts.join(' — ');
}

/**
 * Master-detail body for a billing client: its contacts plus a picker that lists the company's
 * existing billing addresses so one can be referenced for this project.
 *
 * Like ClientContactsEditor it is persistence-agnostic: transition (batch) passes onContactsChange /
 * onBillingAddressChange; the edit form (immediate) additionally passes onContactAdd / onContactRemove
 * to fire per-mutation calls.
 */
export function BillingClientDetail({
  companyId,
  initialContacts = [],
  initialBillingAddressId = null,
  onContactsChange,
  onContactAdd,
  onContactRemove,
  onBillingAddressChange,
}) {
  const apiService = useMemo(() => new ProjectEditorApiService(), []);
  const billingAddressesDataSource = useMemo(
    () => apiService.getBillingAddressesByCompanyDataSource(companyId),
    [apiService, companyId]
  );

  const [billingAddressId, setBillingAddressId] = useState(initialBillingAddressId);

  const handleBillingAddressChanged = useCallback((e) => {
    setBillingAddressId(e.value);
    if (onBillingAddressChange) onBillingAddressChange(e.value);
  }, [onBillingAddressChange]);

  return (
    <div>
      <label style={fieldLabelStyle}>Billing Address</label>
      <SelectBox
        dataSource={billingAddressesDataSource}
        value={billingAddressId}
        valueExpr="id"
        displayExpr={formatBillingAddress}
        searchEnabled={true}
        showClearButton={true}
        placeholder="Select a billing address..."
        onValueChanged={handleBillingAddressChanged}
      />

      <ClientContactsEditor
        companyId={companyId}
        initialContacts={initialContacts}
        onChange={onContactsChange}
        onAdd={onContactAdd}
        onRemove={onContactRemove}
      />
    </div>
  );
}
